import React, { useState, useEffect } from 'react';
import { ActivityIndicator } from '../custom/ActivityIndicator';
import { useAuthProvider } from '../../providers/AuthProvider';

const DefectsOverlay = ({ outstanding, defects, setShowDefects, updateDefect, theme }) => {
    const { isDev, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_defects_edit;

    const [selectedRepair, setSelectedRepair] = useState(null);
    const [repair, setRepair] = useState({});
    const [selectedDefects, setSelectedDefects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        if (!outstanding) {
            setIsLoading(false);
            return;
        }

        const filteredDefects = outstanding.filter((defect) => defects.includes(defect.defect_uuid));
        setSelectedDefects(filteredDefects);
        setIsLoading(false);
    }, [outstanding, defects]);


    return (
        <>
            {isLoading ? (
                <ActivityIndicator />
            ) : (
                <div
                    role="dialog"
                    aria-labelledby="defects-title"
                    aria-describedby="defects-description"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999,
                    }}
                    onClick={() => setShowDefects(false)}
                >
                    <div
                        style={{
                            backgroundColor: theme === 'dark' ? '#333' : '#fff',
                            padding: '20px',
                            borderRadius: '10px',
                            maxWidth: '80%',
                            maxHeight: '80%',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="defects-title">Defects</h3>
                        {selectedDefects.length > 0 ? (
                            selectedDefects.map((defect) => (
                                <div
                                    key={defect.defect_uuid}
                                    style={{
                                        marginBottom: '20px',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div style={{ flex: 1, marginRight: '10px' }}>
                                        <p><strong>Defect UUID:</strong> {defect.defect_uuid}</p>
                                        <p><strong>Type:</strong> {defect.defect_type}</p>
                                        <p><strong>Status:</strong> {defect.status === 'failed' ? 'Unrepaired' : 'Repaired'}</p>
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setSelectedRepair(defect);
                                            setRepair({
                                                defect_uuid: defect.defect_uuid,
                                                vehicle_check_uuid: defect.vehicle_check_uuid,
                                                defect_type: defect.defect_type,
                                                repair_status: defect.status === 'failed' ? 'unrepaired' : 'repaired',
                                                repair_comment: defect.comment || '',
                                            });
                                        }}
                                        style={{ marginTop: '10px' }}
                                        disabled={!canEdit}
                                    >
                                        Repair
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p id="defects-description">No defects available.</p>
                        )}
                    </div>
                </div>
            )}

            {selectedRepair && (
                <div
                    role="dialog"
                    aria-labelledby="repair-title"
                    aria-describedby="repair-description"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                    onClick={() => setSelectedRepair(null)}
                >
                    <div
                        style={{
                            backgroundColor: theme === 'dark' ? '#333' : '#fff',
                            padding: '20px',
                            borderRadius: '10px',
                            width: '400px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="repair-title">Edit Repair</h3>
                        <p><strong>Type:</strong> {selectedRepair.defect_type}</p>
                        <div style={{ marginTop: '10px' }}>
                            <label>Status:</label>
                            <select
                                value={repair.repair_status}
                                onChange={(e) =>
                                    setRepair({
                                        ...repair,
                                        repair_status: e.target.value,
                                    })
                                }
                                style={{
                                    marginTop: '5px',
                                    padding: '5px',
                                    width: '100%',
                                }}
                            >
                                <option value="repaired">Repaired</option>
                                <option value="unrepaired">Unrepaired</option>
                            </select>
                        </div>

                        <div style={{ marginTop: '10px' }}>
                            <label>Comment:</label>
                            <textarea
                                value={repair.repair_comment}
                                onChange={(e) => {
                                    setShowError(false)
                                    setRepair({
                                        ...repair,
                                        repair_comment: e.target.value,
                                    })
                                }}
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '5px',
                                    marginTop: '5px',
                                }}
                            />
                        </div>
                        {showError && (
                            <div style={{ marginTop: '10px', color: '#fc4a4a' }}>
                                Please enter a comment!!
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
                            <button
                                className="btn btn-success"
                                onClick={() => {
                                    // check comments not empty
                                    if (!repair.repair_comment.trim()) {
                                        setShowError(true);
                                        return;
                                    }
                                    const updatedRepair = {
                                        ...selectedRepair,
                                        status: repair.repair_status,
                                        comment: repair.repair_comment,
                                        defect_type: repair.defect_type,
                                    };
                                    updateDefect(updatedRepair);
                                    setSelectedRepair(null);
                                }}
                                style={{ marginTop: '10px' }}
                            >
                                Submit
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setSelectedRepair(null)}
                                style={{ marginLeft: '10px', marginTop: '10px' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DefectsOverlay;
